import React, { useEffect, useState } from "react";
import TeleconsultCard from "../../components/Cards/TeleconsultCard";
import axios from "axios";
import { ConsultWithParticipants } from "../../components/interface/consult";
import LoadingAnimation from "../../components/LoadingAnimation";
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { BASE_URL, getAxiosConfig } from "../../components/interface/Config";
import { apiHandler } from "../../services/authService";

const Teleconsult: React.FC = () => {
  const [history, setHistory] = useState<ConsultWithParticipants[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(true);

  const token = sessionStorage.getItem("authToken");

  const fetchHistory = async (page: number, limit: number) => {
    setLoading(true);
    try {

      // ApiHandler
      const url       = `${BASE_URL}/resource/consults`;
      const payload  = {
        limit,
        page: page + 1, // API expects 1-based index
        searchkey: "",
        order_by: "id",
        dir: 1,
        from_date: "",
        to_date: "",
        participant_ref_number: "",
        consult_status: "",
        consult_id: "",
      }

      if (!token) {
        throw new Error("No auth token found");
      }
      const response  = await apiHandler(token, 'GET', payload, url);

      // const response = await axios.get(
      //   `${BASE_URL}/resource/consults`,
      //   {
      //     params: {
      //       limit,
      //       page: page + 1, // API expects 1-based index
      //       searchkey: "",
      //       order_by: "id",
      //       dir: 1,
      //       from_date: "",
      //       to_date: "",
      //       participant_ref_number: "",
      //       consult_status: "",
      //       consult_id: "",
      //     },
      //      headers: { ...getAxiosConfig().headers }, 
      //   }
      // );

      

      if (!response) {
        throw new Error("No response received from the server");
      }
      console.log("Teleconsult History Response:", response);
      const data = response.data.data;
      const fetchedHistory: ConsultWithParticipants[] = data.consults.map((historyItem: any) => {
        const participants = historyItem.participants || [];
        const publisher = participants.filter((participant: any) => participant.role === "publisher");
        const subscriber = participants.filter((participant: any) => participant.role === "subscriber");

        return {
          id: historyItem.id,
          consult_status: historyItem.consult_status,
          scheduled_at: historyItem.scheduled_at,
          consult_code: historyItem.consult_code,
          consult_type: historyItem.consult_type,
          consult_data: historyItem.consult_data,
          additional_info: historyItem.additional_info,
          started_at: historyItem.started_at,
          ended_at: historyItem.ended_at,
          started_participant_id: historyItem.started_participant_id,
          ended_participant_id: historyItem.ended_participant_id,
          reason: historyItem.reason,
          virtual_service_provider: historyItem.virtual_service_provider,
          followup_date: historyItem.followup_date,
          active: historyItem.active,
          created_at: historyItem.created_at,
          participants: {
            publisher,
            subscriber,
          },
          payment: historyItem.payment,
        };
      });

      setHistory(fetchedHistory);
      setTotalRecords(data.pagination.total || 0);
    } catch (error) {
      console.error("Error fetching teleconsult history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchHistory(currentPage, rowsPerPage);
    }
  }, [currentPage, rowsPerPage, token]);

  const handlePageChange = (newPage: number): void => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(0); // Reset to first page
  };

  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  return (
    <div className="bg-white rounded-lg p-4 mt-10 h-auto flex flex-col ">

      {loading ? (
        <div className="flex justify-center items-center h-full bg-[#eaeaea]">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
      </div>
      ) : history.length > 0 ? (
        <>
          {/* Scrollable Teleconsult Cards Section */}
          <div className="flex-grow mb-6  ">
            <div className="h-[calc(100vh-200px)] overflow-y-auto shadow-lg"> {/* Adjust height as needed */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {history.map((historyItem) => {
                  const patientName =
                    historyItem.participants.subscriber.length > 0
                      ? historyItem.participants.subscriber[0].participant_info.name
                      : "Unknown Participant";
  
                  return (
                    <TeleconsultCard
                      key={historyItem.id}
                      teleconsultHistory={historyItem}
                      participantName={patientName}
                    />
                  );
                })}
              </div>
            </div>
          </div>
  
          {/* Static Pagination Controls */}
          <div className=" flex flex-col sm:flex-row rounded-xl justify-end items-center">
            {/* Rows per page selector */}
            <div className="sm:mb-0">
              <label htmlFor="rowsPerPage">Rows per page:</label>
              <select
                id="rowsPerPage"
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                className="ml-2 border rounded p-1"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value={40}>40</option>
              </select>
            </div>
  
            {/* Page navigation */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(0)}
                disabled={currentPage === 0}
                className="text-sm disabled:opacity-50"
              >
                <FirstPageIcon />
              </button>
  
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0}
                className="px-3 py-1 text-sm disabled:opacity-50"
              >
                <NavigateBeforeIcon />
              </button>
  
              <span className="text-sm">
                Page {currentPage + 1} of {totalPages}
              </span>
  
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
                className="px-3 py-1 text-sm disabled:opacity-50"
              >
                <NavigateNextIcon />
              </button>
  
              <button
                onClick={() => handlePageChange(totalPages - 1)}
                disabled={currentPage === totalPages - 1}
                className="px-3 py-1 text-sm disabled:opacity-50"
              >
                <LastPageIcon />
              </button>
            </div>
          </div>
        </>
      ) : (
        <p className="text-center">No teleconsult history available for the current page.</p>
      )}
    </div>
  );
  
};

export default Teleconsult;
